import React from "react";
import { resolveBackendUrl } from "../../../../helpers/serverUrl";
import { CATEGORY_CODE_ALIASES, REVIEW_REMARK_ISSUE_TAB } from "../constants/assetValuationConstants";

export const pickValidTabValue = (requestedValue, allowedValues, fallbackValue) => {
    const normalizedAllowedValues = allowedValues.filter(Boolean);
    if (normalizedAllowedValues.includes(requestedValue)) return requestedValue;
    return fallbackValue;
};

export const resolveServerFileUrl = (url) => {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    return resolveBackendUrl(url);
};

export const formatDateValue = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString("en-GB");
};

export const formatNumberValue = (value) => {
    if (value === undefined || value === null || value === "") return "-";
    if (typeof value === "number") {
        return value.toLocaleString();
    }
    if (!Number.isNaN(Number(value))) {
        return Number(value).toLocaleString();
    }
    return value;
};

export const parseListResponse = (response) => {
    const payload = response?.data?.data ?? response?.data ?? response ?? [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.content)) return payload.content;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

export const normalizeDisplayValue = (value) => {
    if (
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
    ) {
        return null;
    }

    if (typeof value === "string" || typeof value === "number") {
        return value;
    }

    if (typeof value === "object") {
        return (
            value.name ||
            value.code ||
            value.description ||
            value.locationName ||
            value.label ||
            JSON.stringify(value)
        );
    }

    return String(value);
};

export const renderCellValue = (value) => normalizeDisplayValue(value) ?? "-";

export const toHumanReadableAssetName = (value) => {
    const normalized = normalizeDisplayValue(value);
    if (!normalized || typeof normalized !== "string") return normalized;

    const trimmed = normalized.trim();
    if (!trimmed) return trimmed;

    // Convert enum-style names like COMPUTER_SET or COMPUTER-SET to "Computer Set".
    if (/^[A-Z0-9_-]+$/.test(trimmed)) {
        return trimmed
            .replace(/[_-]+/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    }

    return trimmed;
};

export const isAssetReviewed = (asset) => {
    const raw = asset?.is_reviewed ?? asset?.isReviewed;
    if (raw === true || raw === "true" || raw === 1 || raw === "1") return true;
    return false;
};

export const parseReviewBoolean = (value) => {
    if (value === true || value === "true" || value === 1 || value === "1") return true;
    if (value === false || value === "false" || value === 0 || value === "0") return false;
    return null;
};

export const normalizeReviewStatus = (value) =>
    String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");

export const normalizeCategoryCode = (value) => {
    const normalized = String(value || "")
        .toUpperCase()
        .trim()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");

    return CATEGORY_CODE_ALIASES[normalized] || normalized.replace(/\s+/g, "");
};

export const getReviewNotCompletedRecord = (item) => {
    const raw = item?.raw || item || {};
    return raw.review_not_completed || raw.reviewNotCompleted || item?.review_not_completed || item?.reviewNotCompleted || null;
};

export const isReviewNotCompleted = (item) => {
    const raw = item?.raw || item || {};
    const flagCandidates = [
        raw.is_review_not_completed,
        raw.isReviewNotCompleted,
        item?.is_review_not_completed,
        item?.isReviewNotCompleted,
    ];

    for (const candidate of flagCandidates) {
        const parsed = parseReviewBoolean(candidate);
        if (parsed !== null) return parsed;
    }

    return !!getReviewNotCompletedRecord(item);
};

export const getReviewNotCompletedRemark = (item) => {
    const record = getReviewNotCompletedRecord(item);
    return (
        normalizeDisplayValue(record?.remark || record?.comment || item?.review_not_completed_remark || item?.reviewNotCompletedRemark) ||
        "-"
    );
};

export const getReviewBucket = (item) => {
    const raw = item?.raw || item || {};
    if (isReviewNotCompleted(item)) {
        return isReviewRemarkIssue(item) ? REVIEW_REMARK_ISSUE_TAB : "review-not-completed";
    }

    const directCandidates = [
        raw.is_reviewed,
        raw.isReviewed,
        raw.reviewed,
        raw.reviewed_data,
        raw.reviewedData,
        raw.data_reviewed,
        raw.dataReviewed,
        raw.all_assets_reviewed,
        raw.allAssetsReviewed,
        item?.is_reviewed,
        item?.isReviewed,
        item?.reviewed,
    ];

    for (const candidate of directCandidates) {
        const parsed = parseReviewBoolean(candidate);
        if (parsed === true) return "reviewed";
    }

    const statusCandidates = [
        raw.review_status,
        raw.reviewStatus,
        raw.data_review_status,
        raw.dataReviewStatus,
        item?.review_status,
        item?.reviewStatus,
    ];

    for (const status of statusCandidates) {
        const normalized = normalizeReviewStatus(status);
        if (!normalized) continue;
        if (["reviewed", "approved", "completed"].includes(normalized)) return "reviewed";
        if (["review not completed", "not completed", "incomplete", "needs review later"].includes(normalized)) {
            return "review-not-completed";
        }
        if (["unreviewed", "pending", "not reviewed"].includes(normalized)) return "unreviewed";
    }

    for (const candidate of directCandidates) {
        const parsed = parseReviewBoolean(candidate);
        if (parsed === false) return "unreviewed";
    }

    const normalizedLabel = normalizeReviewStatus(item?.reviewed);
    if (normalizedLabel === "reviewed") return "reviewed";
    if (["review incomplete", "review not completed", "not completed", "incomplete"].includes(normalizedLabel)) {
        return "review-not-completed";
    }
    if (["un reviewed", "unreviewed", "not reviewed", "pending"].includes(normalizedLabel)) {
        return "unreviewed";
    }

    return "unreviewed";
};

export const getReviewBucketLabel = (item) => {
    const bucket = getReviewBucket(item);
    if (bucket === "reviewed") return "Reviewed";
    if (bucket === REVIEW_REMARK_ISSUE_TAB) return "Remark Issue";
    if (bucket === "review-not-completed") return "Review Incomplete";
    return "Un Reviewed";
};

export const isReviewRemarkIssue = (item) => {
    const remark = String(getReviewNotCompletedRemark(item) || "").toLowerCase();
    return (
        remark.startsWith("dl") ||
        remark.includes(" dl") ||
        remark.includes("delete") ||
        remark.includes("deleate") ||
        remark.includes("deleeate") ||
        remark.includes("delet") ||
        remark.includes("delate") ||
        remark.includes("deleted") ||
        remark.includes("deletion") ||
        remark.includes("remove") ||
        remark.includes("removed") ||
        remark.includes("removal") ||
        remark.includes("wrong entry") ||
        remark.includes("invalid entry") ||
        remark.includes("part of building") ||
        remark.includes("repeated asset") ||
        remark.includes("repeat asset") ||
        remark.includes("repeated") ||
        remark.includes("duplicate") ||
        remark.includes("duplicated")
    );
};

export const getValuationCategoryCodes = (valuation) => {
    const raw = valuation?.raw || {};
    const directCode =
        valuation?.categoryCode ||
        valuation?.category_code ||
        valuation?.categoryName ||
        valuation?.category_name ||
        raw.asset_category?.code ||
        raw.asset_category?.name ||
        raw.assetCategory?.code ||
        raw.assetCategory?.name ||
        raw.category?.code ||
        raw.category?.name ||
        raw.asset_category_code ||
        raw.category_code ||
        raw.asset_category_name ||
        raw.category_name;

    const codes = new Set();
    if (directCode) {
        codes.add(normalizeCategoryCode(directCode));
    }

    if (Array.isArray(raw.assets)) {
        raw.assets.forEach((asset) => {
            const code =
                asset?.categoryCode ||
                asset?.category_code ||
                asset?.asset_category?.code ||
                asset?.asset_category?.name ||
                asset?.assetCategory?.code ||
                asset?.assetCategory?.name ||
                asset?.category?.code ||
                asset?.category?.name ||
                asset?.asset_category_code ||
                asset?.category_code ||
                asset?.asset_category_name ||
                asset?.category_name;
            if (code) {
                codes.add(normalizeCategoryCode(code));
            }
        });
    }

    return codes;
};

export const toBooleanLike = (value) => {
    if (value === true || value === "true" || value === 1 || value === "1") return true;
    if (value === false || value === "false" || value === 0 || value === "0") return false;
    return false;
};

export const extractCouncilIdFromAsset = (asset) => {
    const councilId =
        asset?.asset_valuation?.project?.council?.id ||
        asset?.asset_valuation?.project?.council_id ||
        asset?.asset_valuation?.project?.councilId ||
        asset?.valuation?.project?.council?.id ||
        asset?.valuation?.project?.council_id ||
        asset?.valuation?.project?.councilId ||
        asset?.project?.council?.id ||
        asset?.project?.council_id ||
        asset?.project?.councilId;

    return councilId ? String(councilId) : "";
};

export const getCategoryCode = (asset) =>
    normalizeCategoryCode(
        asset.asset_category?.code ||
            asset.asset_category?.name ||
            asset.category?.code ||
            asset.category?.name ||
            asset.assetCategory?.code ||
            asset.assetCategory?.name ||
            asset.asset_category_code ||
            asset.category_code ||
            asset.category_name ||
            asset.asset_category_name ||
            ""
    );

export const createEmptySecurityOverview = () => ({
    id: "",
    accessibility: "",
    street: "",
    landmark: "",
    distance_from_landmark: "",
    neighborhood_type: "",
    neighborhood_density: "",
    amenities: "",
    income_level: "",
    road_type: "",
    distance_from_main_road: "",
    number_of_buildings: null,
});

export const createEmptySiteWorkDraft = (assetId = "", valuationId = "") => ({
    id: "",
    asset_id: assetId,
    valuation_id: valuationId,
    site_work_type: "",
    other_specify: "",
    description: "",
    dimensions: "",
    serial_number: "",
    observed_condition: "",
    remark: "",
    measurement: "",
    construction_rate: "",
});

export const getStatusBadgeVariant = (status) => {
    const normalized = String(status || "").toLowerCase();
    if (normalized === "submitted") return "success";
    if (normalized === "draft") return "warning";
    if (normalized === "rejected") return "danger";
    return "secondary";
};

export const renderStatusBadge = (value) => {
    const displayValue = normalizeDisplayValue(value);
    if (!displayValue) return renderCellValue(value);
    const variant = getStatusBadgeVariant(displayValue);
    return (
        <span className={`badge bg-${variant} rounded-pill text-uppercase`}>
            {displayValue}
        </span>
    );
};

export const createBuildingDraftFromEntity = (building, slotNumber) => ({
    id: building?.id || "",
    is_primary: toBooleanLike(building?.is_primary ?? building?.isPrimary),
    building_type_id:
        building?.building_type_id ||
        building?.buildingTypeId ||
            building?.building_type?.id ||
            "",
        building_name: building?.building_name || building?.buildingName || "",
        other_building_name:
            building?.other_building_name ||
            building?.otherBuildingName ||
            "",
        other_name: "",
        building_number: building?.serial_number || building?.building_number || "",
        no_of_floors: String(building?.no_of_floors ?? building?.noOfFloors ?? ""),
        year_built: String(building?.year_built ?? building?.yearBuilt ?? ""),
        building_measurement: String(
            building?.building_measurement ?? building?.buildingMeasurement ?? ""
        ),
        use_status: building?.use_status || building?.useStatus || "",
        completion_rate: String(building?.completion_rate ?? building?.completionRate ?? ""),
        observed_condition:
            building?.observed_condition ||
            building?.observedCondition ||
            "",
        useful_life_span_years: String(
            building?.useful_life_span_years ?? building?.usefulLifeSpanYears ?? ""
        ),
        construction_rate_per_sqm: String(
            building?.construction_rate_per_sqm ?? building?.constructionRatePerSqm ?? ""
        ),
        sketch_image_url: resolveServerFileUrl(
            building?.sketch_image_url || building?.sketchImageUrl || ""
        ),
        sketch_image_preview: "",
        sketch_image_file: null,
        slotNumber,
    });
